/**
 * VST Vest – Central JavaScript
 * State management, modal system, page router, and all component behaviour.
 */
(function () {
  'use strict';

  var VST = (window.VST = window.VST || {});

  /* ═══════════════════════════════════════════════════════════════
     STATE – single source of truth, pub/sub
     ═══════════════════════════════════════════════════════════════ */
  var _state = {
    style: null,
    variantId: null,
    variantPrice: 0,
    variantAvailable: true,
    capacity: null,
    capacityConfirmed: false,
    activeSport: null,
    harnesses: [],
    accessories: []
  };

  var _subs = [];

  VST.state = _state;

  VST.on = function (fn) {
    _subs.push(fn);
    return fn;
  };

  VST.off = function (fn) {
    _subs = _subs.filter(function (f) { return f !== fn; });
  };

  VST.set = function (key, val) {
    var prev = _state[key];
    if (prev === val) return;
    if (Array.isArray(val) && Array.isArray(prev) && JSON.stringify(prev) === JSON.stringify(val)) return;
    _state[key] = val;
    for (var i = 0; i < _subs.length; i++) _subs[i](key, val, _state);
  };

  /* ═══════════════════════════════════════════════════════════════
     UTILS
     ═══════════════════════════════════════════════════════════════ */
  VST.formatMoney = function (cents) {
    if (typeof theme !== 'undefined' && typeof theme.formatMoney === 'function') {
      return theme.formatMoney(cents, theme.moneyFormat);
    }
    return '$' + (cents / 100).toFixed(2);
  };

  VST.calcTotalPrice = function () {
    var total = _state.variantPrice || 0;
    _state.harnesses.forEach(function (h) { total += h.price || 0; });
    _state.accessories.forEach(function (a) { total += a.price || 0; });
    return total;
  };

  VST.fetchCartAdd = function (items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (r) { return r.json(); });
  };

  /* ═══════════════════════════════════════════════════════════════
     MODAL – generic full-screen modal manager
     ═══════════════════════════════════════════════════════════════ */
  VST.modal = {
    _current: null,

    open: function (id) {
      var el = document.querySelector('[data-vst-modal="' + id + '"]');
      if (!el) return;
      el.classList.add('is-open');
      el.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      el.scrollTop = 0;
      var video = el.querySelector('[data-vst-modal-video]');
      if (video) { try { video.play(); } catch (e) { /* autoplay may be blocked */ } }
      VST.modal._current = el;
    },

    close: function () {
      var el = VST.modal._current || document.querySelector('.vst-modal.is-open');
      if (!el) return;
      el.classList.remove('is-open');
      el.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      var video = el.querySelector('[data-vst-modal-video]');
      if (video) { video.pause(); video.currentTime = 0; }
      VST.modal._current = null;
    }
  };

  /* ═══════════════════════════════════════════════════════════════
     ROUTER – page management with URL state
     Pages: sport-picker | takeover | product
     ═══════════════════════════════════════════════════════════════ */
  VST.router = {
    _pages: {},
    _current: null,

    register: function (name, el) {
      VST.router._pages[name] = el;
    },

    go: function (page, opts) {
      opts = opts || {};
      var url = new URL(window.location.href);

      if (page === 'product' && opts.style) {
        url.searchParams.set('vst_style', opts.style);
        url.searchParams.delete('vst_page');
        if (opts.sport) {
          url.searchParams.set('vst_sport', opts.sport);
        } else {
          url.searchParams.delete('vst_sport');
        }
      } else if (page === 'takeover') {
        url.searchParams.delete('vst_style');
        url.searchParams.delete('vst_sport');
        url.searchParams.set('vst_page', 'choose');
      } else if (page === 'sport-picker') {
        url.searchParams.delete('vst_style');
        url.searchParams.delete('vst_sport');
        url.searchParams.delete('vst_page');
      }

      if (opts.replace) {
        history.replaceState({ vstPage: page }, '', url.toString());
      } else {
        history.pushState({ vstPage: page }, '', url.toString());
      }
      VST.router._show(page);
    },

    current: function () {
      return VST.router._current;
    },

    _show: function (page) {
      VST.router._current = page;
      Object.keys(VST.router._pages).forEach(function (name) {
        var el = VST.router._pages[name];
        if (!el) return;
        if (name === page) {
          el.style.display = '';
          el.classList.remove('is-dismissed');
        } else {
          el.style.display = 'none';
          el.classList.add('is-dismissed');
        }
      });
      document.documentElement.classList.remove('vst-takeover-active');
      if (page === 'product') {
        document.documentElement.classList.add('vst-style-preselected');
      } else {
        document.documentElement.classList.remove('vst-style-preselected');
      }
      window.scrollTo(0, 0);
    },

    _resolveFromUrl: function () {
      var params = new URLSearchParams(window.location.search);
      var style = params.get('vst_style');
      var sport = params.get('vst_sport');
      var vstPage = params.get('vst_page');
      if (style) return { page: 'product', style: style, sport: sport || null };
      if (vstPage === 'choose') return { page: 'takeover' };
      return { page: null };
    }
  };

  /* ═══════════════════════════════════════════════════════════════
     SHARED REFERENCES (set during bootstrap)
     ═══════════════════════════════════════════════════════════════ */
  var _section = null;
  var _productData = null;
  var _variantsMap = {};
  var _typeIndex = 0;
  var _capacityIndex = 1;

  function getVariantById(id) {
    return _variantsMap[id] || null;
  }

  function getOptionInputs(optionName) {
    if (!_section) return [];
    var inputs = _section.querySelectorAll('input[name^="options"]');
    var list = [];
    for (var i = 0; i < inputs.length; i++) {
      if ((inputs[i].getAttribute('name') || '').indexOf(optionName) !== -1) list.push(inputs[i]);
    }
    return list;
  }

  function setOptionValue(optionName, value) {
    var inputs = getOptionInputs(optionName);
    if (inputs.length === 0) return;
    var target = null;
    if (inputs.length === 1 && inputs[0].type === 'hidden') {
      target = inputs[0];
      if (target.value === value) return;
      target.value = value;
    } else {
      for (var i = 0; i < inputs.length; i++) {
        if (inputs[i].value === value) {
          if (inputs[i].checked) return;
          inputs.forEach(function (inp) { inp.checked = false; });
          inputs[i].checked = true;
          target = inputs[i];
          break;
        }
      }
    }
    if (target) target.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* ═══════════════════════════════════════════════════════════════
     COMPONENT: Sticky Add-to-Cart
     ═══════════════════════════════════════════════════════════════ */
  function initStickyATC(el) {
    var priceEl = el.querySelector('[data-vst-total-price]');
    var basePriceEl = el.querySelector('[data-vst-base-price]');
    var accPriceEl = el.querySelector('[data-vst-accessories-price]');
    var breakdownEl = el.querySelector('[data-vst-breakdown]');
    var btn = el.querySelector('.vst-sticky-add-to-cart__btn');
    var btnText = btn ? btn.querySelector('[data-add-to-cart-text]') : null;

    function updatePrice() {
      var base = _state.variantPrice || 0;
      var accTotal = 0;
      _state.harnesses.forEach(function (h) { accTotal += h.price || 0; });
      _state.accessories.forEach(function (a) { accTotal += a.price || 0; });
      var total = base + accTotal;

      if (priceEl) priceEl.textContent = total === 0 ? 'Free' : VST.formatMoney(total);
      if (basePriceEl) basePriceEl.textContent = base === 0 ? 'Free' : VST.formatMoney(base);
      if (accPriceEl) accPriceEl.textContent = VST.formatMoney(accTotal);
      if (breakdownEl) {
        if (accTotal > 0) breakdownEl.removeAttribute('hidden');
        else breakdownEl.setAttribute('hidden', '');
      }
    }

    function updateButton() {
      if (!btn || !btnText) return;
      btn.disabled = !_state.variantAvailable;
      btnText.textContent = _state.variantAvailable ? 'Add to cart' : 'Sold Out';
    }

    function updateVisibility() {
      el.classList.toggle('is-visible', _state.capacityConfirmed);
    }

    VST.on(function (key) {
      if (key === 'variantPrice' || key === 'harnesses' || key === 'accessories') updatePrice();
      if (key === 'variantAvailable') updateButton();
      if (key === 'capacityConfirmed') { updateVisibility(); updateButton(); }
    });

    updatePrice();
    updateButton();
    updateVisibility();

    if (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (btn.disabled) return;

        var variantInput = _section.querySelector('input[name="id"]');
        var mainId = variantInput ? parseInt(variantInput.value, 10) : null;
        if (!mainId) return;

        var isVestMode = _section.classList.contains('vst-vest-mode');
        var items = [{ id: mainId, quantity: 1 }];

        if (!isVestMode) {
          var harnessChecks = _section.querySelectorAll('[data-vst-sidebar-manual] [data-vst-harness-checkbox]:checked');
          if (harnessChecks.length === 0) {
            var harnessError = _section.querySelector('[data-vst-harness-error]');
            if (harnessError) harnessError.classList.add('is-visible');
            var harnessSec = _section.querySelector('[data-vst-harness-selector]');
            if (harnessSec) harnessSec.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
          }
          harnessChecks.forEach(function (cb) {
            if (cb.hasAttribute('data-vst-harness-included')) return;
            items.push({ id: parseInt(cb.value, 10), quantity: 1 });
          });
        }

        _section.querySelectorAll('[data-vst-sidebar-manual] [data-vst-addon-checkbox]:checked').forEach(function (cb) {
          if (!cb.disabled) {
            var addonTitle = (cb.getAttribute('data-addon-title') || '').toLowerCase();
            var qty = addonTitle.indexOf('flask') !== -1 ? 2 : 1;
            items.push({ id: parseInt(cb.value, 10), quantity: qty });
          }
        });

        var origText = btnText ? btnText.textContent : btn.textContent;
        if (btnText) btnText.textContent = 'Adding\u2026';
        btn.disabled = true;

        VST.fetchCartAdd(items)
          .then(function (data) {
            if (data.status && data.status !== 200) {
              if (data.description) alert(data.description);
            } else {
              document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
            }
          })
          .catch(function () { alert('Could not add to cart.'); })
          .finally(function () {
            btn.disabled = false;
            if (btnText) btnText.textContent = origText || 'Add to cart';
          });
      });
    }
  }

  /* ═══════════════════════════════════════════════════════════════
     COMPONENT: Style Takeover
     ═══════════════════════════════════════════════════════════════ */
  function findFirstCapacityForStyle(style) {
    if (!_productData || !_productData.variants) return null;
    for (var i = 0; i < _productData.variants.length; i++) {
      var v = _productData.variants[i];
      if (v.available !== false && v.options[_typeIndex] === style) {
        return v.options[_capacityIndex];
      }
    }
    for (var j = 0; j < _productData.variants.length; j++) {
      var v2 = _productData.variants[j];
      if (v2.options[_typeIndex] === style) return v2.options[_capacityIndex];
    }
    return null;
  }

  function findFirstColorForStyleAndCapacity(style, capacity) {
    if (!_productData || !_productData.variants || _productData.variants[0].options.length < 3) return null;
    var colorIdx = 2;
    for (var i = 0; i < _productData.variants.length; i++) {
      var v = _productData.variants[i];
      if (v.available !== false && v.options[_typeIndex] === style && v.options[_capacityIndex] === capacity) {
        return v.options[colorIdx];
      }
    }
    for (var j = 0; j < _productData.variants.length; j++) {
      var v2 = _productData.variants[j];
      if (v2.options[_typeIndex] === style && v2.options[_capacityIndex] === capacity) return v2.options[colorIdx];
    }
    return null;
  }

  function initStyleTakeover(el) {
    var optionName = el.getAttribute('data-option-name');
    if (!optionName) return;

    el.querySelectorAll('[data-vst-takeover-choose]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var value = this.getAttribute('data-vst-option-value');
        if (!value) return;

        VST.set('capacityConfirmed', false);
        VST.set('activeSport', null);

        _section.querySelectorAll('[data-vst-addon-checkbox]:checked').forEach(function (cb) {
          cb.checked = false;
        });
        var firstAcc = _section.querySelector('[data-vst-addon-checkbox]');
        if (firstAcc) firstAcc.dispatchEvent(new Event('change', { bubbles: true }));

        _section.querySelectorAll('[data-vst-harness-checkbox]').forEach(function (cb) {
          if (!cb.hasAttribute('data-vst-harness-included')) cb.checked = false;
        });
        var firstH = _section.querySelector('[data-vst-harness-checkbox]');
        if (firstH) firstH.dispatchEvent(new Event('change', { bubbles: true }));

        document.dispatchEvent(new CustomEvent('vst:style:will-change', { detail: { type: value }, bubbles: true }));

        var cap = findFirstCapacityForStyle(value);
        setOptionSilent(optionName, value);
        if (cap) {
          var capName = _section.querySelector('[data-vst-capacity-selector]');
          var capOptionName = capName ? capName.getAttribute('data-option-name') : 'Capacity';
          setOptionSilent(capOptionName, cap);

          var color = findFirstColorForStyleAndCapacity(value, cap);
          if (color) {
            var colorSel = _section.querySelector('[data-vst-color-selector]');
            var colorOptionName = colorSel ? colorSel.getAttribute('data-color-option-name') : 'Color';
            setOptionSilent(colorOptionName, color);
          }
        }

        var typeInput = getOptionInputs(optionName)[0];
        if (typeInput) typeInput.dispatchEvent(new Event('change', { bubbles: true }));

        VST.router.go('product', { style: value });
      });
    });

    el.querySelectorAll('[data-vst-explore-trigger]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        e.preventDefault();
        var style = this.getAttribute('data-vst-explore-style');
        if (style) VST.modal.open('explore-' + style);
      });
    });

  }

  /* ═══════════════════════════════════════════════════════════════
     COMPONENT: Sport Picker
     ═══════════════════════════════════════════════════════════════ */
  function setOptionSilent(optionName, value) {
    var inputs = getOptionInputs(optionName);
    if (inputs.length === 0) return null;
    if (inputs.length === 1 && inputs[0].type === 'hidden') {
      inputs[0].value = value;
      return inputs[0];
    }
    for (var i = 0; i < inputs.length; i++) {
      if (inputs[i].value === value) {
        inputs.forEach(function (inp) { inp.checked = false; });
        inputs[i].checked = true;
        return inputs[i];
      }
    }
    return null;
  }

  function applySportPreset(preset, opts) {
    opts = opts || {};
    var targetVariant = preset.variantId ? getVariantById(preset.variantId) : null;
    if (!targetVariant) return null;

    var typeOptionName = opts.typeOptionName || 'Type';
    var capOptionName = opts.capOptionName || 'Capacity';
    var style = targetVariant.options[_typeIndex];

    document.dispatchEvent(new CustomEvent('vst:style:will-change', { detail: { type: style }, bubbles: true }));
    setOptionSilent(typeOptionName, style);

    var capInput = setOptionSilent(capOptionName, targetVariant.options[_capacityIndex]);

    if (targetVariant.options.length > 2) {
      var colorSel = _section.querySelector('[data-vst-color-selector]');
      var colorOptionName = colorSel ? colorSel.getAttribute('data-color-option-name') : 'Color';
      setOptionSilent(colorOptionName, targetVariant.options[2]);
    }

    var typeInput = getOptionInputs(typeOptionName)[0];
    if (typeInput) typeInput.dispatchEvent(new Event('change', { bubbles: true }));
    if (capInput && capInput !== typeInput) capInput.dispatchEvent(new Event('change', { bubbles: true }));

    VST.set('capacityConfirmed', true);

    var harnessIds = preset.harnesses || [];
    _section.querySelectorAll('[data-vst-harness-checkbox]').forEach(function (cb) {
      if (cb.hasAttribute('data-vst-harness-included')) return;
      var pid = parseInt(cb.getAttribute('data-harness-product-id') || '0', 10);
      cb.checked = harnessIds.indexOf(pid) !== -1;
    });
    var firstHarness = _section.querySelector('[data-vst-harness-checkbox]');
    if (firstHarness) firstHarness.dispatchEvent(new Event('change', { bubbles: true }));

    var accessoryIds = preset.accessories || [];
    _section.querySelectorAll('[data-vst-addon-checkbox]').forEach(function (cb) {
      var pid = parseInt(cb.closest('[data-addon-product-id]').getAttribute('data-addon-product-id') || '0', 10);
      cb.checked = accessoryIds.indexOf(pid) !== -1;
    });
    var firstAccessory = _section.querySelector('[data-vst-addon-checkbox]');
    if (firstAccessory) firstAccessory.dispatchEvent(new Event('change', { bubbles: true }));

    return style;
  }

  function initSportPicker(el) {
    var typeOptionName = el.getAttribute('data-type-option-name') || 'Type';
    var capOptionName = el.getAttribute('data-capacity-option-name') || 'Capacity';

    el.querySelectorAll('[data-vst-sport-preset]').forEach(function (card) {
      card.addEventListener('click', function () {
        var raw = this.getAttribute('data-vst-sport-preset');
        var preset;
        try { preset = JSON.parse(raw); } catch (e) { return; }

        var sportName = this.querySelector('.vst-sport-picker__card-name');
        var name = sportName ? sportName.textContent.trim() : '';

        var style = applySportPreset(preset, { typeOptionName: typeOptionName, capOptionName: capOptionName });
        if (!style) return;

        VST.set('activeSport', name);
        VST.router.go('product', { style: style, sport: name });
      });
    });

    var manualBtn = el.querySelector('[data-vst-choose-manually]');
    if (manualBtn) {
      manualBtn.addEventListener('click', function (e) {
        e.preventDefault();
        VST.router.go('takeover');
      });
    }
  }

  /* ═══════════════════════════════════════════════════════════════
     COMPONENT: Sport Sidebar + Sport Switcher
     ═══════════════════════════════════════════════════════════════ */
  function initSportSidebar(el) {
    var manualSidebar = _section.querySelector('[data-vst-sidebar-manual]');
    var descriptionEl = el.querySelector('[data-vst-sport-description]');
    var recommendedContainer = el.querySelector('[data-vst-sport-recommended]');
    var goBackBtn = el.querySelector('[data-vst-sport-go-back]');

    var vestSelectorsWrap = _section.querySelector('[data-vst-vest-selectors]');
    var sportVestOptions = el.querySelector('[data-vst-sport-vest-options]');
    var vestSelectorsAnchor = vestSelectorsWrap ? vestSelectorsWrap.nextElementSibling : null;
    var vestSelectorsParent = vestSelectorsWrap ? vestSelectorsWrap.parentNode : null;

    function restoreVestSelectors() {
      if (!vestSelectorsWrap || !vestSelectorsParent) return;
      if (vestSelectorsAnchor) vestSelectorsParent.insertBefore(vestSelectorsWrap, vestSelectorsAnchor);
      else vestSelectorsParent.appendChild(vestSelectorsWrap);
      if (sportVestOptions) sportVestOptions.style.display = 'none';
    }

    var presetsDataEl = _section.querySelector('[data-vst-sport-presets-data]');
    var presetsData = [];
    if (presetsDataEl) {
      try { presetsData = JSON.parse(presetsDataEl.textContent); } catch (e) { /* ignore */ }
    }

    function getPresetByName(name) {
      for (var i = 0; i < presetsData.length; i++) {
        if (presetsData[i].name === name) return presetsData[i];
      }
      return null;
    }

    function populateRecommended(preset) {
      if (!recommendedContainer) return;
      recommendedContainer.innerHTML = '';

      var ids = [].concat(preset.harnesses || [], preset.accessories || []);

      var harnessCards = _section.querySelectorAll('[data-vst-sidebar-manual] [data-vst-harness-card]');
      harnessCards.forEach(function (card) {
        var cb = card.querySelector('[data-harness-product-id]');
        var pid = cb ? parseInt(cb.getAttribute('data-harness-product-id') || '0', 10) : 0;
        if (ids.indexOf(pid) === -1) return;
        var clone = card.cloneNode(true);
        clone.setAttribute('data-vst-sport-clone', 'harness-' + pid);
        var cloneCb = clone.querySelector('[data-vst-harness-checkbox]');
        if (cloneCb) {
          cloneCb.checked = true;
          cloneCb.disabled = true;
        }
        recommendedContainer.appendChild(clone);
      });

      _section.querySelectorAll('[data-addon-product-id]').forEach(function (card) {
        if (!card.closest('[data-vst-sidebar-manual]')) return;
        var pid = parseInt(card.getAttribute('data-addon-product-id') || '0', 10);
        if (ids.indexOf(pid) === -1) return;
        var clone = card.cloneNode(true);
        clone.setAttribute('data-vst-sport-clone', 'accessory-' + pid);
        var cloneAddonCb = clone.querySelector('[data-vst-addon-checkbox]');
        if (cloneAddonCb) {
          cloneAddonCb.checked = true;
          cloneAddonCb.disabled = false;
          cloneAddonCb.addEventListener('change', function () {
            var realCb = _section.querySelector(
              '[data-vst-sidebar-manual] [data-addon-product-id="' + pid + '"] [data-vst-addon-checkbox]'
            );
            if (realCb) {
              realCb.checked = cloneAddonCb.checked;
              realCb.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
        }
        recommendedContainer.appendChild(clone);
      });
    }

    function showSportView(sportName) {
      var preset = getPresetByName(sportName);
      if (!preset) return;

      if (descriptionEl) descriptionEl.innerHTML = preset.description || '';
      populateRecommended(preset);

      if (vestSelectorsWrap && sportVestOptions) {
        if (_section.classList.contains('vst-vest-mode')) {
          sportVestOptions.appendChild(vestSelectorsWrap);
          sportVestOptions.style.display = '';
        } else {
          restoreVestSelectors();
        }
      }

      if (manualSidebar) manualSidebar.classList.add('vst-sidebar-hidden');
      el.style.display = '';
      if (_section) _section.classList.add('vst-sport-mode');
    }

    if (descriptionEl) {
      descriptionEl.addEventListener('click', function (e) {
        var link = e.target.closest('a[href]');
        if (!link) return;
        var href = link.getAttribute('href') || '';
        var params = new URLSearchParams(href.split('?')[1] || '');
        if (params.get('vst_page') === 'choose') {
          e.preventDefault();
          VST.set('activeSport', null);
          VST.set('capacityConfirmed', false);
          VST.router.go('takeover');
        } else if (params.get('vst_style')) {
          e.preventDefault();
          var style = params.get('vst_style');
          var sport = params.get('vst_sport');
          if (sport) {
            VST.router.go('product', { style: style, sport: sport });
          } else {
            VST.set('activeSport', null);
            VST.router.go('product', { style: style });
          }
        }
      });
    }

    function showManualView() {
      restoreVestSelectors();
      el.style.display = 'none';
      if (manualSidebar) manualSidebar.classList.remove('vst-sidebar-hidden');
      if (_section) _section.classList.remove('vst-sport-mode');
    }

    VST.on(function (key, val) {
      if (key !== 'activeSport') return;
      if (val) {
        showSportView(val);
      } else {
        showManualView();
      }
    });

    if (goBackBtn) {
      goBackBtn.addEventListener('click', function (e) {
        e.preventDefault();
        VST.set('activeSport', null);
        VST.set('capacityConfirmed', false);

        _section.querySelectorAll('[data-vst-addon-checkbox]:checked').forEach(function (cb) {
          cb.checked = false;
        });
        var firstAcc = _section.querySelector('[data-vst-addon-checkbox]');
        if (firstAcc) firstAcc.dispatchEvent(new Event('change', { bubbles: true }));

        _section.querySelectorAll('[data-vst-harness-checkbox]').forEach(function (cb) {
          if (!cb.hasAttribute('data-vst-harness-included')) cb.checked = false;
        });
        var firstH = _section.querySelector('[data-vst-harness-checkbox]');
        if (firstH) firstH.dispatchEvent(new Event('change', { bubbles: true }));

        VST.router.go('takeover');
      });
    }

    initSportSwitcher(el);
  }

  function initSportSwitcher(sportSidebar) {
    var switcher = sportSidebar.querySelector('[data-vst-sport-switcher]');
    if (!switcher) return;

    var pills = switcher.querySelectorAll('[data-sport-name]');

    var presetsDataEl = _section.querySelector('[data-vst-sport-presets-data]');
    var presetsData = [];
    if (presetsDataEl) {
      try { presetsData = JSON.parse(presetsDataEl.textContent); } catch (e) { /* ignore */ }
    }

    function syncActive(name) {
      pills.forEach(function (p) {
        p.classList.toggle('is-active', p.getAttribute('data-sport-name') === name);
      });
    }

    pills.forEach(function (pill) {
      pill.addEventListener('click', function () {
        var sportName = this.getAttribute('data-sport-name');
        if (!sportName) return;

        var preset = null;
        for (var i = 0; i < presetsData.length; i++) {
          if (presetsData[i].name === sportName) { preset = presetsData[i]; break; }
        }
        if (!preset) return;

        var capSel = _section.querySelector('[data-vst-capacity-selector]');
        var typeOptionName = capSel ? (capSel.getAttribute('data-type-option-name') || 'Type') : 'Type';
        var capOptionName = capSel ? (capSel.getAttribute('data-option-name') || 'Capacity') : 'Capacity';

        var style = applySportPreset(preset, { typeOptionName: typeOptionName, capOptionName: capOptionName });
        if (style) {
          VST.set('activeSport', sportName);
          VST.router.go('product', { style: style, sport: sportName, replace: true });
        }
      });
    });

    var wrap = switcher.parentElement;
    var arrowLeft = wrap.querySelector('.vst-sport-switcher__arrow--left');
    var arrowRight = wrap.querySelector('.vst-sport-switcher__arrow--right');

    if (arrowLeft && arrowRight) {
      var SCROLL_STEP = 150;
      var SCROLL_SPEED = 3;
      var SCROLL_INTERVAL = 16;
      var hoverInterval = null;

      function updateArrows() {
        var sl = switcher.scrollLeft;
        var maxScroll = switcher.scrollWidth - switcher.clientWidth;
        arrowLeft.classList.toggle('is-hidden', sl <= 0);
        arrowRight.classList.toggle('is-hidden', sl >= maxScroll - 1);
      }

      function startHoverScroll(dir) {
        stopHoverScroll();
        hoverInterval = setInterval(function () {
          switcher.scrollLeft += dir * SCROLL_SPEED;
          updateArrows();
        }, SCROLL_INTERVAL);
      }

      function stopHoverScroll() {
        if (hoverInterval) { clearInterval(hoverInterval); hoverInterval = null; }
      }

      [arrowLeft, arrowRight].forEach(function (btn) {
        var dir = parseInt(btn.getAttribute('data-dir'), 10);
        btn.addEventListener('click', function () {
          switcher.scrollBy({ left: dir * SCROLL_STEP, behavior: 'smooth' });
        });
        btn.addEventListener('mouseenter', function () { startHoverScroll(dir); });
        btn.addEventListener('mouseleave', stopHoverScroll);
      });

      switcher.addEventListener('scroll', updateArrows, { passive: true });

      var resizeTimer;
      window.addEventListener('resize', function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(updateArrows, 100);
      });

      function scrollToActive(instant) {
        var active = switcher.querySelector('.vst-sport-switcher__pill.is-active');
        if (!active || !switcher.clientWidth) return;
        var pillRect = active.getBoundingClientRect();
        var containerRect = switcher.getBoundingClientRect();
        var pillCenterInContainer = (pillRect.left - containerRect.left) + pillRect.width / 2 + switcher.scrollLeft;
        var target = Math.max(0, pillCenterInContainer - switcher.clientWidth / 2);
        switcher.scrollTo({ left: target, behavior: instant ? 'instant' : 'smooth' });
      }

      var firstActivation = true;

      VST.on(function (key, val) {
        if (key === 'activeSport') {
          syncActive(val);
          var useInstant = firstActivation;
          firstActivation = false;
          requestAnimationFrame(function () {
            scrollToActive(useInstant);
            updateArrows();
          });
        }
      });

      updateArrows();
    } else {
      VST.on(function (key, val) {
        if (key === 'activeSport') syncActive(val);
      });
    }
  }

  /* ═══════════════════════════════════════════════════════════════
     COMPONENT: Capacity Selector
     ═══════════════════════════════════════════════════════════════ */
  function initCapacitySelector(el) {
    var optionName = el.getAttribute('data-option-name');
    var typeOptionName = el.getAttribute('data-type-option-name') || 'Type';
    var typeIdx = parseInt(el.getAttribute('data-type-index') || '0', 10);
    var capIdx = parseInt(el.getAttribute('data-capacity-index') || '1', 10);

    var vestStylesRaw = (el.getAttribute('data-vst-vest-styles') || '').trim();
    var vestStyles = vestStylesRaw ? vestStylesRaw.split(',').map(function (s) { return s.trim(); }) : [];
    var headingEl = el.querySelector('[data-vst-capacity-heading-el]');
    var defaultHeading = el.getAttribute('data-vst-capacity-heading') || '';
    var vestHeading = el.getAttribute('data-vst-vest-heading') || '';

    function isVestStyle(style) {
      if (!style || vestStyles.length === 0) return false;
      for (var i = 0; i < vestStyles.length; i++) {
        if (vestStyles[i].toLowerCase() === style.toLowerCase()) return true;
      }
      return false;
    }

    function applyVestMode(style) {
      var isVest = isVestStyle(style);
      _section.classList.toggle('vst-vest-mode', isVest);
      if (headingEl) headingEl.textContent = isVest ? vestHeading : defaultHeading;

      var colorSel = _section.querySelector('[data-vst-color-selector]');
      if (colorSel) colorSel.style.display = isVest ? '' : 'none';
    }

    function updateVisibility(typeValue) {
      if (!typeValue) return;
      var marker = '|' + typeValue + '|';
      el.querySelectorAll('[data-vst-capacity-value]').forEach(function (btn) {
        var types = btn.getAttribute('data-available-types') || '';
        var avail = types.indexOf(marker) !== -1;
        btn.classList.toggle('vst-capacity-selector__option--hidden', !avail);
        btn.setAttribute('aria-hidden', avail ? 'false' : 'true');
      });
    }

    function syncUI(capValue) {
      el.querySelectorAll('[data-vst-capacity-value]').forEach(function (btn) {
        var sel = btn.getAttribute('data-vst-capacity-value') === capValue;
        btn.classList.toggle('vst-capacity-selector__option--selected', sel);
        btn.classList.toggle('vst-capacity-selector__option--unselected', !sel);
        btn.setAttribute('aria-pressed', sel ? 'true' : 'false');
      });
    }

    el.querySelectorAll('[data-vst-capacity-value]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var value = this.getAttribute('data-vst-capacity-value');
        VST.set('capacityConfirmed', true);
        setOptionValue(optionName, value);
        syncUI(value);
      });
    });

    var helpHeading = el.querySelector('[data-vst-capacity-help-heading]');
    var helpDesc = el.querySelector('[data-vst-capacity-help-desc]');
    var helpHeadingTpl = helpHeading ? helpHeading.textContent : '';
    var helpDescTpl = helpDesc ? helpDesc.textContent : '';
    var modalStyleEl = document.querySelector('[data-vst-help-capacity-style]');

    function updateHelpText(style) {
      if (!style) return;
      if (helpHeading) helpHeading.textContent = helpHeadingTpl.replace(/\(.*?STYLE.*?\)/gi, style).replace(/\(.*?ROLLTOP.*?\)/gi, style);
      if (helpDesc) helpDesc.textContent = helpDescTpl.replace(/\(.*?Style.*?\)/gi, style).replace(/\(.*?Rolltop.*?\)/gi, style);
      if (modalStyleEl) modalStyleEl.textContent = style;
    }

    var helpGrid = document.querySelector('[data-vst-help-capacity-grid]');
    function filterHelpCards(style) {
      if (!helpGrid || !style) return;
      var marker = '|' + style + '|';
      helpGrid.querySelectorAll('.vst-help-capacity__card').forEach(function (card) {
        var styles = card.getAttribute('data-cap-styles') || '';
        card.style.display = styles.indexOf(marker) !== -1 ? '' : 'none';
      });
    }

    VST.on(function (key, val) {
      if (key === 'style') {
        updateVisibility(val);
        updateHelpText(val);
        filterHelpCards(val);
        applyVestMode(val);
      }
      if (key === 'capacityConfirmed') {
        if (val === false) {
          syncUI(null);
        } else {
          setTimeout(function () {
            var v = getVariantById(_state.variantId);
            if (v && v.options && v.options[capIdx] !== undefined) syncUI(v.options[capIdx]);
          }, 0);
        }
      }
      if (key === 'variantId') {
        var v = getVariantById(val);
        if (v && v.options) {
          updateVisibility(v.options[typeIdx]);
          updateHelpText(v.options[typeIdx]);
          filterHelpCards(v.options[typeIdx]);
          applyVestMode(v.options[typeIdx]);
          if (_state.capacityConfirmed && v.options[capIdx] !== undefined) syncUI(v.options[capIdx]);
        }
      }
    });

    document.addEventListener('vst:style:will-change', function (evt) {
      var typeValue = evt.detail && evt.detail.type;
      if (typeValue) {
        updateVisibility(typeValue);
        applyVestMode(typeValue);
      }
    });

    var v = getVariantById(_state.variantId);
    if (v && v.options) {
      updateVisibility(v.options[typeIdx]);
      updateHelpText(v.options[typeIdx]);
      filterHelpCards(v.options[typeIdx]);
      applyVestMode(v.options[typeIdx]);
    }
  }

  /* ═══════════════════════════════════════════════════════════════
     COMPONENT: Harness Selector
     ═══════════════════════════════════════════════════════════════ */
  function initHarnessSelector(el) {
    var checkboxes = el.querySelectorAll('[data-vst-harness-checkbox]');
    var errorEl = el.querySelector('[data-vst-harness-error]');

    function syncState() {
      var list = [];
      checkboxes.forEach(function (cb) {
        if (cb.checked && !cb.hasAttribute('data-vst-harness-included')) {
          list.push({
            productId: parseInt(cb.getAttribute('data-harness-product-id') || '0', 10),
            variantId: parseInt(cb.value, 10),
            price: parseInt(cb.getAttribute('data-variant-price') || '0', 10),
            title: cb.getAttribute('data-harness-title') || '',
            imageUrl: cb.getAttribute('data-harness-image') || ''
          });
        }
      });
      VST.set('harnesses', list);
    }

    function validate() {
      var hasIncluded = !!el.querySelector('[data-vst-harness-included]');
      var valid = hasIncluded || _state.harnesses.length >= 1;
      if (errorEl) errorEl.classList.toggle('is-visible', !valid);
      return valid;
    }

    checkboxes.forEach(function (cb) {
      cb.addEventListener('change', function () {
        syncState();
        validate();
      });
    });

    syncState();

    document.addEventListener('theme:cart:add', function (evt) {
      var button = evt.detail && evt.detail.button;
      if (!button || !_section || !_section.contains(button)) return;
      var hasIncluded = !!el.querySelector('[data-vst-harness-included]');
      if (!hasIncluded && _state.harnesses.length === 0) {
        if (evt.preventDefault) evt.preventDefault();
        if (evt.stopImmediatePropagation) evt.stopImmediatePropagation();
        if (errorEl) errorEl.classList.add('is-visible');
      }
    }, true);
  }

  /* ═══════════════════════════════════════════════════════════════
     COMPONENT: Accessories Selector
     ═══════════════════════════════════════════════════════════════ */
  function initAccessoriesSelector(el) {
    var cards = el.querySelectorAll('[data-vst-accessory-card]');

    cards.forEach(function (card) {
      var cb = card.querySelector('[data-vst-addon-checkbox]');
      if (!cb) return;
      var title = cb.getAttribute('data-addon-title') || '';
      if (title.toLowerCase().indexOf('flask') === -1) return;
      var titleEl = card.querySelector('.vst-accessory-card__title');
      if (titleEl) titleEl.textContent = title + ' X2';
      var unitPrice = parseInt(cb.getAttribute('data-variant-price') || '0', 10);
      var priceEl = card.querySelector('.vst-accessory-card__price');
      if (priceEl) priceEl.textContent = VST.formatMoney(unitPrice * 2);
    });

    function updateCompatibility() {
      var variantId = _state.variantId;
      var harnessProductIds = _state.harnesses.map(function (h) { return h.productId; });
      if (_section && !_section.classList.contains('vst-vest-mode')) {
        var includedH = _section.querySelector('[data-vst-harness-included]');
        if (includedH) {
          var incPid = parseInt(includedH.getAttribute('data-harness-product-id') || '0', 10);
          if (incPid && harnessProductIds.indexOf(incPid) === -1) harnessProductIds.push(incPid);
        }
      }

      cards.forEach(function (card) {
        var compatVariantsAttr = card.getAttribute('data-compatible-variants') || '';
        var compatHarnessesAttr = card.getAttribute('data-compatible-harnesses') || '';

        var variantOk = !compatVariantsAttr || compatVariantsAttr.split(',').indexOf(String(variantId)) !== -1;
        var harnessOk = !compatHarnessesAttr || harnessProductIds.some(function (pid) {
          return compatHarnessesAttr.split(',').indexOf(String(pid)) !== -1;
        });

        var isCompat = variantOk && harnessOk;
        card.classList.toggle('is-incompatible', !isCompat);
        var hideWhenIncompat = card.getAttribute('data-hide-when-incompatible') === 'true';
        card.classList.toggle('is-hidden-incompatible', !isCompat && hideWhenIncompat);
        var cb = card.querySelector('[data-vst-addon-checkbox]');
        if (cb) {
          cb.disabled = !isCompat;
          if (!isCompat && cb.checked) {
            cb.checked = false;
            syncState();
          }
        }
      });

      var optionsContainer = el.querySelector('.vst-accessories-selector__options');
      if (optionsContainer) {
        var allCards = Array.prototype.slice.call(optionsContainer.querySelectorAll('[data-vst-accessory-card]'));
        allCards.forEach(function (card) {
          if (card.classList.contains('is-incompatible')) {
            optionsContainer.appendChild(card);
          }
        });
      }
    }

    function syncState() {
      var list = [];
      el.querySelectorAll('[data-vst-addon-checkbox]:checked').forEach(function (cb) {
        if (cb.disabled) return;
        var title = cb.getAttribute('data-addon-title') || '';
        var unitPrice = parseInt(cb.getAttribute('data-variant-price') || '0', 10);
        var isFlask = title.toLowerCase().indexOf('flask') !== -1;
        list.push({
          productId: parseInt(cb.getAttribute('data-addon-product-id') || '0', 10),
          variantId: parseInt(cb.value, 10),
          price: isFlask ? unitPrice * 2 : unitPrice,
          title: isFlask ? title + ' X2' : title,
          imageUrl: cb.getAttribute('data-addon-image-url') || ''
        });
      });
      VST.set('accessories', list);
    }

    el.addEventListener('change', function (e) {
      if (e.target && e.target.hasAttribute('data-vst-addon-checkbox')) syncState();
    });

    VST.on(function (key) {
      if (key === 'variantId' || key === 'harnesses') updateCompatibility();
    });

    updateCompatibility();
  }

  /* ═══════════════════════════════════════════════════════════════
     COMPONENT: Color Selector (vest mode only)
     ═══════════════════════════════════════════════════════════════ */
  function initColorSelector(el) {
    var colorOptionName = el.getAttribute('data-color-option-name');
    var colorIdx = parseInt(el.getAttribute('data-color-index') || '2', 10);
    var typeIdx = parseInt(el.getAttribute('data-type-index') || '0', 10);

    function filterByType(typeValue) {
      if (!typeValue) return;
      var marker = '|' + typeValue + '|';
      el.querySelectorAll('[data-vst-color-value]').forEach(function (btn) {
        var types = btn.getAttribute('data-available-types') || '';
        var avail = types.indexOf(marker) !== -1;
        btn.classList.toggle('vst-color-selector__option--hidden', !avail);
        btn.setAttribute('aria-hidden', avail ? 'false' : 'true');
      });
    }

    function syncUI(colorValue) {
      el.querySelectorAll('[data-vst-color-value]').forEach(function (btn) {
        var sel = btn.getAttribute('data-vst-color-value') === colorValue;
        btn.classList.toggle('vst-color-selector__option--selected', sel);
        btn.setAttribute('aria-pressed', sel ? 'true' : 'false');
      });
    }

    el.querySelectorAll('[data-vst-color-value]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var value = this.getAttribute('data-vst-color-value');
        setOptionValue(colorOptionName, value);
        syncUI(value);
      });
    });

    VST.on(function (key, val) {
      if (key === 'style') {
        filterByType(val);
      }
      if (key === 'variantId') {
        var v = getVariantById(val);
        if (v && v.options) {
          filterByType(v.options[typeIdx]);
          if (v.options[colorIdx] !== undefined) syncUI(v.options[colorIdx]);
        }
      }
    });

    document.addEventListener('vst:style:will-change', function (evt) {
      var typeValue = evt.detail && evt.detail.type;
      if (typeValue) filterByType(typeValue);
    });

    var v = getVariantById(_state.variantId);
    if (v && v.options) {
      filterByType(v.options[typeIdx]);
      if (v.options[colorIdx] !== undefined) syncUI(v.options[colorIdx]);
    }
  }

  /* ═══════════════════════════════════════════════════════════════
     COMPONENT: Image Set Views
     ═══════════════════════════════════════════════════════════════ */
  function initImageViewer(el) {
    var display = el.querySelector('[data-vst-image-set-display]');
    var nav = el.querySelector('.vst-image-actions');
    var buttons, slides;
    var accBtn = el.querySelector('[data-vst-view="accessories"]');
    var accBadge = accBtn ? accBtn.querySelector('[data-vst-accessories-badge]') : null;
    var accCurrentIndex = 0;
    var accMode = false;
    var accSlideEl = null;
    var accListEl = null;
    if (accBtn && accBtn.parentNode) {
      var accWrap = document.createElement('div');
      accWrap.className = 'vst-image-actions__btn-group';
      accBtn.parentNode.insertBefore(accWrap, accBtn);
      accWrap.appendChild(accBtn);
      accListEl = document.createElement('div');
      accListEl.className = 'vst-image-actions__acc-list';
      accWrap.appendChild(accListEl);
    }

    var harnBtn = el.querySelector('[data-vst-view="harness"]');
    var harnBadge = harnBtn ? harnBtn.querySelector('[data-vst-harness-badge]') : null;
    var harnCurrentIndex = 0;
    var harnMode = false;
    var harnSlideEl = null;
    var harnListEl = null;
    if (harnBtn && harnBtn.parentNode) {
      var harnWrap = document.createElement('div');
      harnWrap.className = 'vst-image-actions__btn-group';
      harnBtn.parentNode.insertBefore(harnWrap, harnBtn);
      harnWrap.appendChild(harnBtn);
      harnListEl = document.createElement('div');
      harnListEl.className = 'vst-image-actions__acc-list';
      harnWrap.appendChild(harnListEl);
    }

    var rotateBtn = el.querySelector('[data-vst-view="rotate"]');
    var videoSlideEl = null;
    var activityBtns = [];

    var styleVideos = {};
    var videosEl = el.querySelector('[data-vst-style-videos]');
    if (videosEl) { try { styleVideos = JSON.parse(videosEl.textContent); } catch (e) {} }

    var allImageSets = [];
    var setsEl = el.querySelector('[data-vst-all-image-sets]');
    if (setsEl) { try { allImageSets = JSON.parse(setsEl.textContent); } catch (e) {} }

    function refreshRefs() {
      buttons = nav ? nav.querySelectorAll('.vst-image-actions__btn[data-vst-label-index]') : [];
      slides = display ? display.querySelectorAll('.vst-image-set-display__slide:not(.vst-accessory-slide):not(.vst-harness-slide)') : [];
    }
    refreshRefs();

    function clearActive() {
      buttons.forEach(function (b) { b.classList.remove('is-active'); b.setAttribute('aria-pressed', 'false'); });
      if (accBtn) { accBtn.classList.remove('is-active'); accBtn.setAttribute('aria-pressed', 'false'); }
      if (harnBtn) { harnBtn.classList.remove('is-active'); harnBtn.setAttribute('aria-pressed', 'false'); }
      if (rotateBtn) { rotateBtn.classList.remove('is-active'); rotateBtn.setAttribute('aria-pressed', 'false'); }
      activityBtns.forEach(function (b) {
        b.classList.remove('is-active'); b.setAttribute('aria-pressed', 'false');
      });
    }

    function hideAccSlide() { if (accSlideEl) accSlideEl.classList.remove('is-active'); }
    function hideHarnSlide() { if (harnSlideEl) harnSlideEl.classList.remove('is-active'); }

    var fullscreenOverlay = null;

    function hideVideoSlide() {
      if (videoSlideEl) {
        var vid = videoSlideEl.querySelector('video');
        if (vid) { vid.pause(); vid.removeAttribute('src'); vid.load(); }
        videoSlideEl.classList.remove('is-active');
      }
      if (fullscreenOverlay && fullscreenOverlay.parentNode) {
        var fsVid = fullscreenOverlay.querySelector('video');
        if (fsVid) { fsVid.pause(); fsVid.removeAttribute('src'); fsVid.load(); }
        fullscreenOverlay.parentNode.removeChild(fullscreenOverlay);
        fullscreenOverlay = null;
      }
      if (display) display.style.visibility = '';
      var wrap = el.closest('.product__images-wrap');
      if (wrap) wrap.classList.remove('vst-video-playing');
    }

    function showLabel(index) {
      accMode = false;
      harnMode = false;
      var idx = parseInt(index, 10);
      clearActive();
      buttons.forEach(function (b) {
        var match = b.getAttribute('data-vst-label-index') === String(idx);
        b.classList.toggle('is-active', match);
        b.setAttribute('aria-pressed', match ? 'true' : 'false');
      });
      slides.forEach(function (s) {
        s.style.visibility = '';
        s.classList.toggle('is-active', s.getAttribute('data-vst-label-index') === String(idx));
      });
      hideAccSlide();
      hideHarnSlide();
      hideVideoSlide();
      syncAccListState();
      syncHarnListState();
    }

    function findImageSet(variantId) {
      for (var i = 0; i < allImageSets.length; i++) {
        for (var j = 0; j < allImageSets[i].variant_ids.length; j++) {
          if (allImageSets[i].variant_ids[j] === variantId) return allImageSets[i];
        }
      }
      return null;
    }

    var svgIcon = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><line x1="6" y1="12" x2="11" y2="12" stroke="white" stroke-width="2"/><line x1="12" y1="18" x2="12" y2="13" stroke="white" stroke-width="2"/><line x1="13" y1="12" x2="18" y2="12" stroke="white" stroke-width="2"/><line x1="12" y1="11" x2="12" y2="6" stroke="white" stroke-width="2"/><circle cx="12" cy="12" r="11" stroke="white" stroke-width="2"/></svg>';

    function rebuild(imageSet) {
      if (nav) nav.querySelectorAll('.vst-image-actions__btn[data-vst-label-index]').forEach(function (b) { b.remove(); });
      if (display) display.querySelectorAll('.vst-image-set-display__slide:not(.vst-accessory-slide):not(.vst-video-slide):not(.vst-harness-slide)').forEach(function (s) { s.remove(); });

      var labels = imageSet ? imageSet.labels : [];
      var refNode = nav ? nav.firstChild : null;

      labels.forEach(function (item, i) {
        if (nav) {
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'vst-image-actions__btn' + (i === 0 ? ' is-active' : '');
          btn.setAttribute('data-vst-label-index', i);
          btn.setAttribute('data-vst-view', 'label');
          btn.setAttribute('aria-pressed', i === 0 ? 'true' : 'false');
          btn.innerHTML = '<span class="vst-image-actions__icon">' + svgIcon + '</span><span>' + item.label + '</span>';
          nav.insertBefore(btn, refNode);
          btn.addEventListener('click', function () { showLabel(i); });
        }
        if (display) {
          var slide = document.createElement('div');
          slide.className = 'vst-image-set-display__slide' + (i === 0 ? ' is-active' : '');
          slide.setAttribute('data-vst-label-index', i);
          slide.setAttribute('role', 'img');
          slide.setAttribute('aria-label', item.label);
          if (item.image_url) slide.innerHTML = '<img src="' + item.image_url + '" alt="' + item.label + '" width="800" loading="lazy">';
          var accEl = display.querySelector('.vst-accessory-slide');
          if (accEl) display.insertBefore(slide, accEl);
          else display.appendChild(slide);
        }
      });
      refreshRefs();
    }

    function ensureAccSlide() {
      if (!display) return null;
      if (!accSlideEl) {
        accSlideEl = document.createElement('div');
        accSlideEl.className = 'vst-image-set-display__slide vst-accessory-slide';
        accSlideEl.setAttribute('role', 'img');
        accSlideEl.innerHTML = '<img src="" alt="" loading="lazy">';
        display.appendChild(accSlideEl);
      }
      return accSlideEl;
    }

    function showAccImage(item) {
      var sl = ensureAccSlide();
      if (!sl) return;
      var img = sl.querySelector('img');
      if (img) { img.src = item.imageUrl; img.alt = item.title; }
      slides.forEach(function (s, i) {
        if (i === 0) { s.classList.add('is-active'); s.style.visibility = 'hidden'; }
        else s.classList.remove('is-active');
      });
      hideVideoSlide();
      hideHarnSlide();
      sl.classList.add('is-active');
    }

    function updateAccButton() {
      var items = _state.accessories.filter(function (a) { return a.imageUrl; });
      var count = items.length;
      if (accBtn) accBtn.style.display = count > 0 ? '' : 'none';
      if (accBadge) accBadge.textContent = count;
      if (count === 0 && accMode) { accMode = false; if (slides.length) showLabel(0); }
      rebuildAccList(items);
    }

    function rebuildAccList(items) {
      if (!accListEl) return;
      accListEl.innerHTML = '';
      accListEl.classList.toggle('is-visible', items.length > 0);
      items.forEach(function (item, idx) {
        var link = document.createElement('button');
        link.type = 'button';
        link.className = 'vst-image-actions__acc-list-item';
        link.textContent = item.title;
        link.addEventListener('click', function (e) {
          e.preventDefault();
          accMode = true;
          accCurrentIndex = idx;
          clearActive();
          accBtn.classList.add('is-active');
          accBtn.setAttribute('aria-pressed', 'true');
          showAccImage(item);
          syncAccListState();
        });
        accListEl.appendChild(link);
      });
      syncAccListState();
    }

    function syncAccListState() {
      if (!accListEl) return;
      accListEl.classList.toggle('is-active', accMode);
      var links = accListEl.querySelectorAll('.vst-image-actions__acc-list-item');
      links.forEach(function (link, idx) {
        link.classList.toggle('is-current', accMode && idx === accCurrentIndex);
      });
    }

    function syncHarnListState() {
      if (!harnListEl) return;
      harnListEl.classList.toggle('is-active', harnMode);
      var links = harnListEl.querySelectorAll('.vst-image-actions__acc-list-item');
      links.forEach(function (link, idx) {
        link.classList.toggle('is-current', harnMode && idx === harnCurrentIndex);
      });
    }

    function ensureHarnSlide() {
      if (!display) return null;
      if (!harnSlideEl) {
        harnSlideEl = document.createElement('div');
        harnSlideEl.className = 'vst-image-set-display__slide vst-harness-slide';
        harnSlideEl.setAttribute('role', 'img');
        harnSlideEl.innerHTML = '<img src="" alt="" loading="lazy">';
        display.appendChild(harnSlideEl);
      }
      return harnSlideEl;
    }

    function showHarnImage(item) {
      var sl = ensureHarnSlide();
      if (!sl) return;
      var img = sl.querySelector('img');
      if (img) { img.src = item.imageUrl; img.alt = item.title; }
      slides.forEach(function (s, i) {
        if (i === 0) { s.classList.add('is-active'); s.style.visibility = 'hidden'; }
        else s.classList.remove('is-active');
      });
      hideVideoSlide();
      hideAccSlide();
      sl.classList.add('is-active');
    }

    function updateHarnButton() {
      var items = _state.harnesses.filter(function (h) { return h.imageUrl; });
      var count = items.length;
      if (harnBtn) harnBtn.style.display = count > 0 ? '' : 'none';
      if (harnBadge) {
        harnBadge.textContent = count;
        harnBadge.style.display = count > 1 ? '' : 'none';
      }
      if (count === 0 && harnMode) { harnMode = false; if (slides.length) showLabel(0); }
      rebuildHarnList(items);
    }

    function rebuildHarnList(items) {
      if (!harnListEl) return;
      harnListEl.innerHTML = '';
      harnListEl.classList.toggle('is-visible', items.length > 0);
      items.forEach(function (item, idx) {
        var link = document.createElement('button');
        link.type = 'button';
        link.className = 'vst-image-actions__acc-list-item';
        link.textContent = item.title;
        link.addEventListener('click', function (e) {
          e.preventDefault();
          harnMode = true;
          accMode = false;
          harnCurrentIndex = idx;
          clearActive();
          harnBtn.classList.add('is-active');
          harnBtn.setAttribute('aria-pressed', 'true');
          showHarnImage(item);
          syncHarnListState();
          syncAccListState();
        });
        harnListEl.appendChild(link);
      });
      syncHarnListState();
    }

    if (harnBtn) {
      harnBtn.addEventListener('click', function (e) {
        e.preventDefault();
        var items = _state.harnesses.filter(function (h) { return h.imageUrl; });
        if (items.length === 0) return;
        accMode = false;
        if (!harnMode) { harnMode = true; harnCurrentIndex = 0; }
        else harnCurrentIndex = (harnCurrentIndex + 1) % items.length;
        clearActive();
        harnBtn.classList.add('is-active');
        harnBtn.setAttribute('aria-pressed', 'true');
        showHarnImage(items[harnCurrentIndex]);
        syncAccListState();
        syncHarnListState();
      });
    }

    function showRotateVideo() {
      var videos = styleVideos[_state.style];
      if (!videos || !videos.rotate) return;
      accMode = false;
      harnMode = false;
      clearActive();
      hideAccSlide();
      hideHarnSlide();
      hideVideoSlide();
      syncAccListState();
      syncHarnListState();

      slides.forEach(function (s, i) {
        if (i === 0) { s.classList.add('is-active'); s.style.visibility = 'hidden'; }
        else s.classList.remove('is-active');
      });
      if (!videoSlideEl) {
        videoSlideEl = document.createElement('div');
        videoSlideEl.className = 'vst-image-set-display__slide vst-video-slide';
        display.appendChild(videoSlideEl);
      }
      videoSlideEl.innerHTML = '<video src="' + videos.rotate + '" autoplay muted loop playsinline></video>';
      videoSlideEl.classList.add('is-active');
      if (rotateBtn) { rotateBtn.classList.add('is-active'); rotateBtn.setAttribute('aria-pressed', 'true'); }
    }

    function showActivityVideo(entry, btn) {
      accMode = false;
      harnMode = false;
      clearActive();
      hideAccSlide();
      hideHarnSlide();
      hideVideoSlide();
      syncAccListState();
      syncHarnListState();

      if (slides.length) { slides[0].classList.add('is-active'); }
      display.style.visibility = 'hidden';
      fullscreenOverlay = document.createElement('div');
      fullscreenOverlay.className = 'vst-fullscreen-video-overlay';
      fullscreenOverlay.innerHTML = '<video src="' + entry.url + '" autoplay muted loop playsinline></video>';
      el.appendChild(fullscreenOverlay);
      var wrap = el.closest('.product__images-wrap');
      if (wrap) wrap.classList.add('vst-video-playing');
      if (btn) { btn.classList.add('is-active'); btn.setAttribute('aria-pressed', 'true'); }
    }

    function updateVideoButtons() {
      var videos = styleVideos[_state.style] || {};
      if (rotateBtn) rotateBtn.style.display = videos.rotate ? '' : 'none';

      activityBtns.forEach(function (b) { b.remove(); });
      activityBtns = [];

      var activities = videos.activities || [];
      var refNode = (harnBtn && harnBtn.closest('.vst-image-actions__btn-group')) || (accBtn && accBtn.closest('.vst-image-actions__btn-group')) || null;
      activities.forEach(function (entry) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'vst-image-actions__btn';
        btn.setAttribute('data-vst-activity-btn', '');
        btn.innerHTML = '<span class="vst-image-actions__icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16.3682 11.293L9.68136 17.9778" stroke="white" stroke-width="2"/><path d="M16.3711 12.7065L9.73813 6.07473" stroke="white" stroke-width="2"/><circle cx="12" cy="12" r="11" stroke="white" stroke-width="2"/></svg></span><span>' + entry.label + '</span>';
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          showActivityVideo(entry, btn);
        });
        if (refNode && nav) {
          nav.insertBefore(btn, refNode);
        } else if (nav) {
          nav.appendChild(btn);
        }
        activityBtns.push(btn);
      });
    }

    if (rotateBtn) rotateBtn.addEventListener('click', function (e) { e.preventDefault(); showRotateVideo(); });

    if (accBtn) {
      accBtn.addEventListener('click', function (e) {
        e.preventDefault();
        var items = _state.accessories.filter(function (a) { return a.imageUrl; });
        if (items.length === 0) return;
        if (!accMode) { accMode = true; harnMode = false; accCurrentIndex = 0; }
        else accCurrentIndex = (accCurrentIndex + 1) % items.length;
        clearActive();
        accBtn.classList.add('is-active');
        accBtn.setAttribute('aria-pressed', 'true');
        showAccImage(items[accCurrentIndex]);
        syncAccListState();
        syncHarnListState();
      });
    }

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var idx = this.getAttribute('data-vst-label-index');
        if (idx != null) showLabel(idx);
      });
    });

    VST.on(function (key, val) {
      if (key === 'variantId') {
        var imageSet = findImageSet(val);
        rebuild(imageSet);
        accMode = false;
        harnMode = false;
        hideAccSlide();
        hideHarnSlide();
        hideVideoSlide();
        updateAccButton();
        updateHarnButton();
      }
      if (key === 'style') updateVideoButtons();
      if (key === 'accessories') updateAccButton();
      if (key === 'harnesses') updateHarnButton();
    });

    updateAccButton();
    updateHarnButton();
    updateVideoButtons();
  }

  /* ═══════════════════════════════════════════════════════════════
     COMPONENT: Image Actions Overflow Fix
     Keeps `.product__images-wrap` tall enough to contain the
     absolutely-positioned `.vst-image-actions` nav as the user
     adds harnesses/accessories. The wrap centers its flex children
     vertically, so growing `min-height` alone would push the content
     down. We add the overflow as `padding-bottom` AND `min-height`
     in equal amounts: the padding pulls content up by X/2, the
     min-height pushes it down by X/2, the two cancel, and the wrap
     grows only at the bottom.
     ═══════════════════════════════════════════════════════════════ */
  function initImageActionsHeightFix(wrap, actions) {
    var BUFFER = 24;
    var originalPaddingBottom = parseFloat(getComputedStyle(wrap).paddingBottom) || 0;
    var appliedDelta = 0;

    function update() {
      var wrapRect = wrap.getBoundingClientRect();
      var actionsRect = actions.getBoundingClientRect();
      var actionsBottomInWrap = actionsRect.bottom - wrapRect.top;
      var currentH = wrap.offsetHeight;

      var overflow = actionsBottomInWrap + BUFFER - currentH;
      var newDelta = appliedDelta + overflow;
      if (newDelta < 0) newDelta = 0;

      if (Math.abs(newDelta - appliedDelta) < 0.5) return;
      appliedDelta = newDelta;

      var headerH = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) || 69;
      var floor = window.innerHeight - headerH;
      wrap.style.minHeight = (floor + appliedDelta) + 'px';
      wrap.style.paddingBottom = (originalPaddingBottom + appliedDelta) + 'px';
    }

    if (typeof ResizeObserver !== 'undefined') {
      var ro = new ResizeObserver(update);
      ro.observe(actions);
    }

    var lastWidth = window.innerWidth;
    window.addEventListener('resize', function () {
      if (window.innerWidth === lastWidth) return;
      lastWidth = window.innerWidth;
      update();
    });
    setTimeout(update, 0);
  }

  /* ═══════════════════════════════════════════════════════════════
     BOOTSTRAP
     ═══════════════════════════════════════════════════════════════ */
  document.addEventListener('DOMContentLoaded', function () {
    _section = document.querySelector('product-component[data-section-type="product"]');
    if (!_section) return;

    _section.querySelectorAll('.vst-modal').forEach(function (modal) {
      document.body.appendChild(modal);
    });

    var jsonEl = _section.querySelector('[data-product-json]');
    if (jsonEl) {
      try { _productData = JSON.parse(jsonEl.textContent); } catch (e) {}
    }
    if (_productData && _productData.variants) {
      _productData.variants.forEach(function (v) { _variantsMap[v.id] = v; });
    }

    var styleNameEl = _section.querySelector('[data-vst-style-name]');
    _typeIndex = styleNameEl ? parseInt(styleNameEl.getAttribute('data-type-option-index') || '0', 10) : 0;
    _capacityIndex = (_typeIndex === 0) ? 1 : 0;

    var variantIdInput = _section.querySelector('input[name="id"]');
    if (variantIdInput) {
      var initialId = parseInt(variantIdInput.value, 10);
      var initialVariant = getVariantById(initialId);
      if (initialVariant) {
        _state.variantId = initialVariant.id;
        _state.variantPrice = initialVariant.price;
        _state.variantAvailable = initialVariant.available;
        if (initialVariant.options) {
          _state.style = initialVariant.options[_typeIndex] || null;
          _state.capacity = initialVariant.options[_capacityIndex] || null;
        }
      }
    }

    function onVariantChange(variant) {
      if (!variant) return;
      VST.set('variantId', variant.id);
      VST.set('variantPrice', variant.price);
      VST.set('variantAvailable', variant.available);
      if (variant.options) {
        VST.set('style', variant.options[_typeIndex]);
        VST.set('capacity', variant.options[_capacityIndex]);
      }
      if (styleNameEl && variant.options && variant.options[_typeIndex] !== undefined) {
        styleNameEl.textContent = variant.options[_typeIndex];
      }
    }

    document.addEventListener('theme:variant:change', function (evt) {
      var v = evt.detail && evt.detail.variant;
      if (v) onVariantChange(v);
    });

    if (variantIdInput) {
      var obs = new MutationObserver(function () {
        var v = getVariantById(parseInt(variantIdInput.value, 10));
        if (v) onVariantChange(v);
      });
      obs.observe(variantIdInput, { attributes: true, attributeFilter: ['value'] });
      variantIdInput.addEventListener('change', function () {
        var v = getVariantById(parseInt(variantIdInput.value, 10));
        if (v) onVariantChange(v);
      });
    }

    /* ── Init components ── */
    var stickyAtc = _section.querySelector('[data-vst-sticky-atc]');
    if (stickyAtc) initStickyATC(stickyAtc);

    var takeover = _section.querySelector('[data-vst-takeover]');
    if (takeover) initStyleTakeover(takeover);

    var sportPicker = _section.querySelector('[data-vst-sport-picker]');
    if (sportPicker) initSportPicker(sportPicker);

    var sportSidebar = _section.querySelector('[data-vst-sidebar-sport]');
    if (sportSidebar) initSportSidebar(sportSidebar);

    var capacitySel = _section.querySelector('[data-vst-capacity-selector]');
    if (capacitySel) initCapacitySelector(capacitySel);

    var harnessSel = _section.querySelector('[data-vst-harness-selector]');
    if (harnessSel) initHarnessSelector(harnessSel);

    var accessoriesSel = _section.querySelector('[data-vst-accessories-selector]');
    if (accessoriesSel) initAccessoriesSelector(accessoriesSel);

    var colorSel = _section.querySelector('[data-vst-color-selector]');
    if (colorSel) initColorSelector(colorSel);

    var imageViewer = _section.querySelector('[data-vst-image-set-views]');
    if (imageViewer) initImageViewer(imageViewer);

    var imagesWrap = _section.querySelector('.product__images-wrap');
    var imageActions = _section.querySelector('.vst-image-actions');
    if (imagesWrap && imageActions) initImageActionsHeightFix(imagesWrap, imageActions);

    /* ── Vest size guide CM/IN toggle ── */
    var sizeGuide = document.querySelector('[data-vst-vest-size-guide]');
    if (sizeGuide) {
      var sgBtns = sizeGuide.querySelectorAll('.vst-size-guide__unit-btn');
      var sgTable = sizeGuide.querySelector('.vst-size-guide__table');
      sgBtns.forEach(function (btn) {
        btn.addEventListener('click', function () {
          sgBtns.forEach(function (b) { b.classList.remove('is-active'); });
          btn.classList.add('is-active');
          var unit = btn.getAttribute('data-unit');
          if (!sgTable) return;
          sgTable.querySelectorAll('tbody td[data-cm]').forEach(function (td) {
            var cm = parseFloat(td.getAttribute('data-cm'));
            if (unit === 'cm') {
              td.textContent = cm + ' cm';
            } else {
              td.textContent = (cm / 2.54).toFixed(1) + ' in';
            }
          });
        });
      });
    }

    /* ── Router setup ── */
    var productContent = _section.querySelector('[data-vst-product-content]');
    if (takeover) VST.router.register('takeover', takeover);
    if (sportPicker) VST.router.register('sport-picker', sportPicker);
    if (productContent) VST.router.register('product', productContent);

    function getPresetsData() {
      var el = _section.querySelector('[data-vst-sport-presets-data]');
      if (!el) return [];
      try { return JSON.parse(el.textContent); } catch (e) { return []; }
    }

    function restoreSport(sportName) {
      var presets = getPresetsData();
      var preset = null;
      for (var i = 0; i < presets.length; i++) {
        if (presets[i].name === sportName) { preset = presets[i]; break; }
      }
      if (!preset) return false;

      var capSel = _section.querySelector('[data-vst-capacity-selector]');
      var tName = capSel ? (capSel.getAttribute('data-type-option-name') || 'Type') : 'Type';
      var cName = capSel ? (capSel.getAttribute('data-option-name') || 'Capacity') : 'Capacity';

      applySportPreset(preset, { typeOptionName: tName, capOptionName: cName });
      VST.set('activeSport', sportName);
      return true;
    }

    var resolved = VST.router._resolveFromUrl();
    if (resolved.page === 'product' && resolved.style) {
      if (resolved.sport) {
        restoreSport(resolved.sport);
      } else {
        var typeOptionName = takeover ? takeover.getAttribute('data-option-name') : 'Type';
        setOptionValue(typeOptionName, resolved.style);
      }
      VST.router._show('product');
    } else if (resolved.page === 'takeover') {
      VST.router._show('takeover');
    } else {
      var defaultPage = sportPicker ? 'sport-picker' : (takeover ? 'takeover' : 'product');
      VST.router._show(defaultPage);
    }

    window.addEventListener('popstate', function () {
      var r = VST.router._resolveFromUrl();
      if (r.page === 'product' && r.style) {
        if (r.sport) {
          restoreSport(r.sport);
        } else {
          var optName = takeover ? takeover.getAttribute('data-option-name') : 'Type';
          setOptionValue(optName, r.style);
          VST.set('activeSport', null);
        }
        VST.router._show('product');
      } else if (r.page === 'takeover') {
        VST.set('activeSport', null);
        VST.router._show('takeover');
      } else {
        VST.set('activeSport', null);
        VST.router._show(sportPicker ? 'sport-picker' : 'takeover');
      }
    });

    /* ── Modal global handlers ── */
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') VST.modal.close();
    });

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-vst-modal-close]')) {
        VST.modal.close();
      }
      var trigger = e.target.closest('[data-vst-modal-open]');
      if (trigger) {
        e.preventDefault();
        e.stopPropagation();
        var id = trigger.getAttribute('data-vst-modal-open');
        if (id) VST.modal.open(id);
      }
    });
  });
})();
